import { ProductType } from 'src/type/product';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Dialog,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFSwitch, RHFTextField, RHFUploadBox } from 'src/components/hook-form';
import { uploadImage } from 'src/libs/firebase/firebase-upload';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import { fData } from 'src/utils/format-number';
import * as Yup from 'yup';

const Schema = Yup.object().shape({
  name: Yup.string().required('กรุณากรอกชื่อสินค้า'),
  description: Yup.string(),
  image: Yup.mixed<File | string>().nullable(),
  isEnable: Yup.boolean(),
  isOrnament: Yup.boolean(),
});

const defaultValues = {
  name: '',
  description: '',
  image: null,
  isEnable: true,
  isOrnament: false,
};

type Props = {
  open: boolean;
  onClose: VoidFunction;
  product: ProductType | null;
  handleReload: VoidFunction;
};

export default function ProductFormDialog({ open, onClose, product, handleReload }: Props) {
  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (open && product) {
      reset({
        name: product.name,
        description: product.description,
        isEnable: product.isEnable,
        isOrnament: product.isOrnament,
        image: product.imageUrl,
      });
    } else {
      reset(defaultValues);
    }
  }, [product?.id, open]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      let imageUrl = '';

      if (typeof values.image === 'string') {
        imageUrl = values.image;
      } else if (values.image) {
        imageUrl = await uploadImage(values.image, 'images');
      }

      const body = {
        name: values.name,
        description: values.description,
        imageUrl,
        isEnable: values.isEnable,
        isOrnament: values.isOrnament,
      };

      if (product) {
        await axios.patch(endpoints.product.update(product.id), body);
      } else {
        await axios.post(endpoints.product.post, body);
      }

      handleReload();
      beforeClose();
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  const handleDropImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('image', newFile, { shouldValidate: true, shouldTouch: true });
      }
    },
    [setValue]
  );

  return (
    <Dialog open={open} onClose={beforeClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>{product ? 'แก้ไข' : 'สร้าง'}สินค้า</DialogTitle>

        <DialogContent>
          <Stack spacing={3}>
            <RHFUploadBox
              name="image"
              maxSize={3145728}
              onDrop={handleDropImage}
              placeholder="อัพโหลดรูปสินค้า"
              sx={{
                maxHeight: 140,
                width: '100%',
                height: '100%',
                aspectRatio: '1/1',
                mx: 'auto',
              }}
              helperText={
                <>
                  อนุญาตไฟล์ *.jpeg, *.jpg, *.png, *.gif
                  <br /> ขนาดสูงสุด {fData(3145728)}
                </>
              }
            />

            <RHFTextField
              name="name"
              placeholder="ชื่อสินค้า"
              InputLabelProps={{ shrink: true }}
              disabled={!!product}
            />

            <RHFTextField
              multiline
              rows={3}
              name="description"
              placeholder="รายละเอียด"
              InputLabelProps={{ shrink: true }}
            />

            <RHFSwitch
              name="isEnable"
              label={
                <Stack>
                  <Typography variant="body2">เปิดใช้งาน</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ผู้ใช้งานสามารถเห็นสินค้านี้ได้
                  </Typography>
                </Stack>
              }
            />

            <RHFSwitch
              name="isOrnament"
              label={
                <Stack>
                  <Typography variant="body2">ทองคำรูปพรรณ</Typography>
                  <Typography variant="caption" color="text.secondary">
                    สินค้านี้เป็นทองคำรูปพรรณ (ใช้การคำนวณ 1 บาท เท่ากับ 15.2 กรัม)
                  </Typography>
                </Stack>
              }
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
            บันทึก
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
